<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-16">
      <Icon name="tabler:loader-2" size="40" class="animate-spin text-muted-foreground" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="reports.length === 0"
      class="rounded-2xl bg-card py-12 text-center shadow-sm"
    >
      <Icon
        name="tabler:flag-off"
        size="48"
        class="mx-auto mb-3 text-muted-foreground/50"
      />
      <p class="text-base font-medium text-foreground">{{ labels.empty }}</p>
      <p class="mt-1 text-sm text-muted-foreground">{{ labels.emptyDescription }}</p>
    </div>

    <!-- Reports list -->
    <div v-else class="space-y-3">
      <div
        v-for="report in reports"
        :key="report._id"
        class="rounded-2xl bg-card p-4 shadow-sm"
      >
        <!-- Header: status badge + content type + date -->
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-wrap items-center gap-2">
            <Badge :variant="statusVariant(report.status)">
              {{ labels.statuses[report.status] ?? report.status }}
            </Badge>
            <span class="text-xs text-muted-foreground">
              {{ labels.contentTypes[report.contentType] ?? report.contentType }}
            </span>
          </div>
          <span class="shrink-0 text-xs text-muted-foreground">
            {{ formatRelativeDate(report.createdAt) }}
          </span>
        </div>

        <!-- Reason -->
        <p class="mt-2 text-sm font-medium text-foreground">
          {{ labels.reasons[report.reason] ?? report.reason }}
        </p>

        <!-- User-provided description -->
        <p
          v-if="report.description"
          class="mt-1 line-clamp-3 text-sm text-muted-foreground"
        >
          {{ report.description }}
        </p>

        <!-- Admin notes / action taken (shown after review) -->
        <div
          v-if="report.adminNotes || report.actionTaken"
          class="mt-3 rounded-lg bg-muted/50 p-3"
        >
          <p class="text-sm text-muted-foreground">
            {{ report.adminNotes ?? report.actionTaken }}
          </p>
        </div>

        <!-- Reporter notified indicator (DSA Art. 16(6)) -->
        <div
          v-if="report.reporterNotifiedAt"
          class="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <Icon name="tabler:circle-check" size="14" class="shrink-0 text-success" />
          <span>{{ labels.notified }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Badge from '../ui/badge/Badge.vue';
import type { BadgeVariants } from '../ui/badge/badgeVariants';
import { formatRelativeDate } from '../../utils/formatters';

export interface ContentReport {
  _id: string;
  contentType: string;
  reason: string;
  description?: string;
  status: string;
  adminNotes?: string;
  actionTaken?: string;
  reporterNotifiedAt?: number;
  createdAt: number;
}

export interface MyReportsLabels {
  empty: string;
  emptyDescription: string;
  notified: string;
  statuses: Record<string, string>;
  reasons: Record<string, string>;
  contentTypes: Record<string, string>;
}

defineProps<{
  reports: ContentReport[];
  isLoading: boolean;
  labels: MyReportsLabels;
}>();

function statusVariant(status: string): BadgeVariants['variant'] {
  const map: Record<string, BadgeVariants['variant']> = {
    pending: 'warning',
    under_review: 'info',
    actioned: 'success',
    dismissed: 'secondary',
  };
  return map[status] ?? 'outline';
}
</script>
